import { computed, ref, toRef, watchEffect } from 'vue';
import type { HighlightsDataItem } from '@type/aem';
import { getResponsiveUSPImage } from './responsiveImg';
import { type PictureProps } from '@dh-io-owpi/shared/src/components/ResponsiveImg';
import type { VehicleStyle as VehicleStyleBase } from '@dh-io-owpi/backend-api/src/data-contracts';
import { useAemData } from '@/composables/useAemData';
import { isLoaderDataProvided } from '@/composables/useLoaderData';

import type { VehicleStyle } from '@/lib/api';
import { usePageStateStore } from '@/stores/pageState';
import { type ValidationNotification } from '@dh-io-owpi/shared/src/components/ErrorList/ValidationNotification';
import { useSpaErrors } from '@/stores/spaErrors';
import { useSectionsStore } from '@/stores/sections';

const maxHighlights = 5;

interface BaseHighlightsDataItem extends HighlightsDataItem {
  slug: string;
}

/** Encapsulate all the highlights logic */
export function useUspHighlights() {
  const { highlights } = useAemData();
  if (!highlights) return ref([]);

  const extendedHighlights = highlights.map((highlight, i) => ({
    ...highlight,
    slug: `highlight-${i}`,
  }));

  if (!isLoaderDataProvided()) {
    useSpaErrors().setErrors([
      {
        mode: 'warning',
        message: `No styles found: "style whitelist" is being ignored. The first 5 highlights are being shown.`,
      },
    ]);

    return ref(mapHighlightsFromAemData(extendedHighlights.slice(0, maxHighlights)));
  }

  const sectionStore = useSectionsStore();

  const pageState = usePageStateStore();
  const stylesForBrand = pageState.availableStyles.filter((style) => style.brand === pageState.brandId);
  const currentStyle = toRef(pageState, 'style');
  const uspMap: Record<string, USPContentFragment[]> = {};
  for (const style of stylesForBrand) {
    uspMap[style.name] = mapHighlightsFromAemData(
      extendedHighlights.filter((_, index) => style.uspIndexes.includes(index)),
    );
  }

  const styleSpecificUsps = computed(() => uspMap[currentStyle.value.name] ?? []);

  // Hide the USP section, if no USPs exist for the current style
  watchEffect(() => {
    if (styleSpecificUsps.value.length === 0) {
      sectionStore.setVisibility('usp', false);
    }
  });
  return styleSpecificUsps;
}

/**
 * Maps the highlights defined by the author to the styles our API returns.
 * If no styles are provided, all highlights are mapped to the no style key.
 * This function will also validate the whitelist of the highlights and return a list of validation errors.
 */
export function extractHighlightsFromAemDataToStyles(
  highlights: HighlightsDataItem[] | undefined,
  styles: VehicleStyleBase[],
): [uspMap: Record<string, number[]>, validationErrors: ValidationNotification[]] {
  if (!highlights) return [{}, []];

  const styleMap: Record<string, StyleUspsIndexed> = {};

  const errors: Errors = {
    invalid: [],
    maxUsp: [],
  };

  const validationErrors: ValidationNotification[] = [];
  for (let index = 0; index < highlights.length; index++) {
    const highlight = highlights[index];
    const whitelist = splitWhitelist(highlight.whitelist);

    // initialize the list of invalid styles with all the styles in the whitelist
    const invalidStyles = new Set(whitelist);

    for (const style of styles) {
      const uspID = style.name;
      const map = (styleMap[uspID] ??= { id: uspID, name: style.name, indexes: [] });
      // remove this style from the list of invalid styles
      if (whitelist.length) invalidStyles.delete(uspID);
      // add this highlight to the highlights of this style if no whitelist was provided or it includes this style
      if (!whitelist.length || whitelist.includes(uspID)) {
        map.indexes.push(index);
      }
    }

    if (invalidStyles.size) errors.invalid.push({ usp: highlight.title, style: `${[...invalidStyles]}` });
  }

  const uspMap: Record<string, number[]> = {};

  for (const [id, s] of Object.entries(styleMap)) {
    uspMap[id] = s.indexes.slice(0, maxHighlights);

    if (s.indexes.length > maxHighlights) errors.maxUsp.push(s.name);
  }

  if (errors.invalid.length) {
    const styleErrorMessages = errors.invalid.map((i) => `"${i.usp}" (refers to style(s) "${i.style}")`).join('\n');
    validationErrors.push({
      mode: 'error',
      message: `Some highlights are referring to styles that are inexistent or not yet available. The following highlights will not be shown unless the styles become available:\n${styleErrorMessages}`,
    });
  }

  if (errors.maxUsp.length) {
    const styleErrorMessages = errors.maxUsp.map((m) => `"${m}"`).join(', ');
    validationErrors.push({
      mode: 'warning',
      message: `Limit of highlights exceeded for this style(s). Only the first 5 highlights are being shown for this style(s):\n${styleErrorMessages}`,
    });
  }

  return [uspMap, validationErrors];
}

interface Errors {
  invalid: { usp: string; style: string }[];
  maxUsp: string[];
}

export const mapHighlightsFromAemData = (highlights: BaseHighlightsDataItem[]): USPContentFragment[] =>
  highlights.slice(0, maxHighlights).map<USPContentFragment>((highlight, i, items) => ({
    slug: highlight.slug,
    title: highlight.title,
    description: highlight.description,
    footnote: highlight.footnote,
    media: {
      type: 'image', // if we support video we must change this
      picture: getResponsiveUSPImage(highlight.media, (items.length != 3 && i == 0) || (items.length == 4 && i == 3)),
      altText: highlight.mediaAltText || highlight.title,
    },
  }));

/** splits whitelist using comma, trim and filter out empty strings */
const splitWhitelist = (whitelist?: string): string[] =>
  whitelist
    ?.split(',')
    .map((s) => s.trim())
    .filter((s) => s != '') ?? [];

/** encapsulate the logic to set the uspID on the style. At the moment is just the name as-is */
export const addUspsToStyle = (style: VehicleStyleBase, uspIndexes: number[]): VehicleStyle => ({
  ...style,
  uspIndexes,
});

type StyleUspsIndexed = {
  id: string;
  name: string;
  indexes: number[];
};

export interface PiSpaHighlightsResponse {
  data: PiSpaHighlightsData;
}

export interface PiSpaHighlightsData {
  piSpaHighlightsList: PiSpaHighlightsList;
}

export interface PiSpaHighlightsList {
  items: PiSpaHighlight[];
}

export interface PiSpaHighlight {
  _path: string;
  title: string;
  image: Image;
  description: Description;
  modelSeries: string;
  subbrand: string;
}

export interface Image {
  _authorUrl: string;
  _publishUrl: string;
  type: 'image' | 'multimedia';
  format?: string;
}

export interface Description {
  html: string;
}

/**
 * minimal data needed to render a USP content fragment
 */
export interface USPContentFragment {
  slug: string;
  title: string;
  description: string;
  footnote?: string;
  media: USPMedia;
}

type USPMedia = {
  type: 'image';
  picture: PictureProps;
  altText: string;
};
// | {
//     type: 'video';
//     format: string;
//     url: string;
//     altText: string;
//   };
